import Image from 'next/image';
import React, { useState, useEffect } from 'react';
import Button from '@/atoms/Button';
import Input from '@/atoms/Input';
import Radio from '@/atoms/Radio';
import routes from '@/constants/routes/index';
import { useRouter } from 'next/router';
import RightArrow from '@/public/icons/arrow-right.svg';
import LeftArrow from '@/public/icons/arrow-left.svg';
import { AxiosError } from 'axios';
import { BrokerPersonalDetails } from './types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { brokerDetailsSchema } from '@/helpers/validators';
import { useMutation } from 'react-query';
import { createBroker } from '@/services/api';
import { useNavigationContext } from '@/contexts/navigationContext/NavigationContext';
import { defaultApplicant } from '@/contexts/navigationContext/constants/brokerDetails';
import getConfig from 'next/config';

const options = [
  { value: 'DIRECTLY_AUTHORISED', caption: 'Direct' },
  { value: 'APPOINTED_REPRESENTATIVE', caption: 'Appointed' },
  { value: 'OTHER', caption: 'Neither' },
];

const professionOptions = [
  { value: 'MORTGAGE_BROKER', caption: 'Mortgage Broker/Advisor' },
  { value: 'FINANCIAL_ADVISOR', caption: 'Financial Advisor' },
  { value: 'ESTATE_AGENT', caption: 'Estate Agent' },
  { value: 'OTHER', caption: 'Other' },
];

const BrokerDetails = () => {
  const router = useRouter();
  const { publicRuntimeConfig } = getConfig();
  const { createNavigationData } = useNavigationContext();

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    getValues,
    setError,
  } = useForm<BrokerPersonalDetails>({
    resolver: yupResolver(brokerDetailsSchema),
    mode: 'onTouched',
  });

  const [isMutating, setIsMutating] = useState(false);

  const contact_numberWatch = watch('contact_number');

  const { mutate } = useMutation(createBroker, {
    onSuccess: (data) => {
      if (data) router.push(routes.contactUs);
    },
    onError: (err: AxiosError) => {
      if (err.isAxiosError) {
        if (
          (err?.response?.data as any).context.errors &&
          (err?.response?.data as any).context.errors[1] ===
            'email does not conform to the "email" format'
        ) {
          setError('email', { message: 'Enter a valid email' });
        }

        if ((err?.response?.data as any).error === 'email_already_in_use') {
          setError('email', {
            message:
              'This email has already been used to register for an account with Keyzy. Please get in touch with us if you can’t access your account.',
          });
        }
      }
    },
    onMutate: () => setIsMutating(true),
    onSettled: () => setIsMutating(false),
  });

  useEffect(() => {
    createNavigationData([], defaultApplicant);
  }, [[], createNavigationData]);

  const onSubmit = async (data: BrokerPersonalDetails) => {
    mutate({ broker: data });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="on">
        <div className="flex flex-col mx-auto mb-20">
          <h1 className="flex flex-col items-center mb-10 text-3xl font-bold md:flex-row md:text-4xl md:mb-16">
            <Image
              src="/icons/personal-details.svg"
              width={64}
              height={64}
              alt="personal details"
            />
            <div className="text-3xl md:ml-3 mt-4 md:mt-0">Your details</div>
          </h1>

          <div className="w-full px-4 pt-6 pb-6 mx-auto mb-8 bg-white shadow sm:px-6 md:mb-16 md:pt-12 md:px-12">
            <Input
              type="text"
              label="First name"
              placeholder="e.g. Julie"
              {...register('first_name', { required: true })}
              error={errors.first_name ? errors.first_name.message : ''}
            />
            <Input
              type="text"
              label="Last name"
              placeholder="e.g. Smith"
              {...register('last_name', { required: true })}
              error={errors.last_name ? errors.last_name.message : ''}
            />

            <div>
              <Input
                type="text"
                label="Email address"
                placeholder="e.g. julie.smith@example.com"
                {...register('email', { required: true })}
                error={errors.email ? errors.email.message : ''}
              />
            </div>

            <div>
              <Input
                type="text"
                label="Company name"
                placeholder="e.g. A Mortgage Company"
                {...register('company_name', { required: true })}
                error={errors.company_name ? errors.company_name.message : ''}
              />
            </div>
            <div>
              <div className="-mb-2 text-lg font-bold font">
                How would you describe your profession?
              </div>
              <Radio
                options={professionOptions}
                {...register('profession', { required: true })}
                error={errors.profession && errors.profession.message}
                className="basis-[calc(100%)] md:basis-[calc(47%)] last:mr-3 even:mr-0"
              />
            </div>
            <div>
              <div className="-mb-2 text-lg font-bold font">
                Are you directly authorised or an appointed representative?
              </div>
              <Radio
                options={options}
                {...register('type', { required: true })}
                error={errors.type && errors.type.message}
                className="w-1/2"
              />
            </div>
            <div>
              <Input
                type="text"
                label="FCA Reference Number"
                isOptional
                format="######"
                thousandSeparator={true}
                placeholder="e.g. 123456"
                {...register('fca_number')}
                error={errors.fca_number ? errors.fca_number.message : ''}
              />
            </div>
            <div>
              <Input
                type="text"
                label="Which network or packager are you affilliated with?"
                placeholder="e.g. A Network Company"
                isOptional
                {...register('network_name')}
                error={errors.network_name ? errors.network_name.message : ''}
              />
            </div>
            <div className="md:w-64">
              <Input
                type="text"
                label="Mobile number"
                format="+44 (0) ####-######"
                mask="_"
                allowEmptyFormatting
                isNumericString={false}
                value={getValues('contact_number')}
                className={`${
                  !(
                    !!contact_numberWatch &&
                    contact_numberWatch !== '+44 (0) ____-______'
                  ) && 'text-grey-500'
                }`}
                {...register('contact_number')}
                error={
                  (errors.contact_number && errors.contact_number.message) || ''
                }
              />
            </div>
          </div>
          <div className="flex flex-row-reverse flex-wrap justify-between sm:flex-nowrap">
            <Button
              type="submit"
              disabled={false}
              isLoading={isMutating}
              suffix={<RightArrow className="md:ml-3" />}
            >
              Submit
            </Button>
            <Button
              prefix={<LeftArrow className="md:mr-3" />}
              type="button"
              variant="outline"
              onClick={() =>
                router.push(
                  publicRuntimeConfig.NEXT_CUSTOMER_APP + '/intermediaries',
                )
              }
            >
              Go back
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default BrokerDetails;
